import { useEffect, useRef, useCallback } from 'react';

/**
 * Radix modal focus-return hook.
 *
 * When any of our modals opens, we capture whatever DOM element had
 * focus (usually the tile / button the user pressed). When the modal
 * closes, `handleCloseAutoFocus` restores focus to that element,
 * completing the motion choreography as a full round-trip so keyboard
 * users don't get dumped onto <body>.
 *
 * Usage:
 * ```jsx
 * const { handleCloseAutoFocus } = useTriggerFocusReturn(open);
 * // ...
 * <DialogContent onCloseAutoFocus={handleCloseAutoFocus} ... />
 * ```
 *
 * Passing an untruthy `open` (or omitting entirely) makes the hook a
 * no-op, which keeps the semantics safe if a modal accidentally
 * mounts before its parent is ready.
 */
export function useTriggerFocusReturn(open) {
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const el = document.activeElement;
    // Skip capturing meaningless anchors — Safari mobile parks
    // activeElement on <body> after a touch, and we never want to
    // "restore" focus to that.
    if (el && el !== document.body && el.tagName !== 'HTML') {
      triggerRef.current = el;
    } else {
      triggerRef.current = null;
    }
  }, [open]);

  const handleCloseAutoFocus = useCallback((e) => {
    const t = triggerRef.current;
    if (t && typeof document !== 'undefined' && document.contains(t)) {
      e.preventDefault();
      try {
        t.focus({ preventScroll: false });
      } catch (_err) {
        /* ignore focus errors on detached / restricted nodes */
      }
    }
  }, []);

  return { handleCloseAutoFocus };
}
